import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Logo } from '@/components/viz/Logo';
import { posthog } from '@/lib/posthog';
import type { NarrativeBarShareMode } from '@/components/layout/NarrativeBar';
import { authUrlWithShare } from './auth-share-url';

export interface PublicChromeShellProps {
  /** The variant body — `<SignalOperatingAsset>` or `<SignalNewDevelopment>`. */
  children: ReactNode;
  /** Reserved for future analytics; surfaced as a `data-share-id` attribute. */
  shareId?: string;
  /**
   * Optional non-scrolling slot rendered between the header and the
   * main body — `external-sharing` Phase 5 Session 5.3 (2026-05-09)
   * uses it to mount `<DealTabBar>` (Signal | Model). When omitted, the
   * shell renders header → body → footer unchanged (Phase 6 default
   * behavior; the auth-callback bridge route also relies on this).
   */
  tabBar?: ReactNode;
  /**
   * Optional composer slot rendered as a SIBLING of `<main>` (between the
   * scroll area and the footer) — NOT inside `<main>`. The public-deal
   * `<NarrativeBar>` mounts here so its `absolute bottom-full` expanded overlay
   * (and the sticky collapse caret at the overlay's top) is NOT clipped by
   * `<main>`'s `overflow-y-auto`. Mounting it inside `<main>` clipped the top
   * ~20px of the overlay (the collapse caret showed as a thin sliver), surfaced
   * 2026-06-04. Clipped only by the chrome-shell (page bounds) now.
   */
  composer?: ReactNode;
  /**
   * `external-sharing` Phase 5 Pass-3-walkthrough fix (2026-05-09).
   * Drives the footer's Sign-Up CTA visibility:
   *   - `'external-anon'` (default when omitted): footer prompt visible
   *     (Phase 6 conversion-CTA behavior).
   *   - `'external-authed'`: footer prompt visible (forwardee may still
   *     want to share with others, even if they're already authed).
   *   - `'internal-recipient'`: footer prompt HIDDEN (recipient is
   *     already an Arcanum user; Sign-Up CTA is irrelevant noise).
   *   - `'owner'`: footer prompt HIDDEN (owner is previewing their own
   *     deal; Sign-Up CTA is irrelevant noise).
   */
  shareMode?: NarrativeBarShareMode;
  className?: string;
}

/**
 * `agent-ux-optimization` Phase 6 Session 6 (2026-05-08); redesigned
 * `deal-signal-overhaul` Phase 5 (2026-05-12).
 *
 * Public deal-link wrapper rendered at `/deal/:shareId` for unauthenticated
 * (or non-owner authenticated) viewers. Minimal-chrome surface.
 *
 * Layout (post-2026-05-12 redesign):
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Logo [SHARED DEAL LINK]        [UNLOCK FULL ARCANUM ACCESS] │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │                                                             │
 *   │                    <variant body>                           │
 *   │                                                             │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │                                           Built on Arcanum  │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * The bottom Unlock card was removed; conversion CTA now lives in the
 * header (top-right, gated to external viewers). Footer is a slim
 * hairline with the "Built on Arcanum" attribution only.
 *
 * Pure presentation; the variant dispatch lives in `routes/deal.tsx`.
 */
export function PublicChromeShell({
  children,
  shareId,
  tabBar,
  composer,
  shareMode,
  className,
}: PublicChromeShellProps) {
  // Show the header CTA for any viewer who isn't already an internal
  // recipient or the owner — same visibility derivation that previously
  // drove the (now-removed) footer prompt.
  const showHeaderCta =
    shareMode == null ||
    shareMode === 'external-anon' ||
    shareMode === 'external-authed';
  return (
    <div
      data-testid="public-chrome-shell"
      data-share-id={shareId}
      data-share-mode={shareMode}
      className={clsx(
        'flex flex-col h-screen bg-void font-body text-text overflow-hidden',
        className
      )}
    >
      <header
        data-testid="public-chrome-header"
        className="flex items-center justify-between px-6 py-3 border-b border-panel-border bg-bg shrink-0"
      >
        <div className="flex items-center gap-3">
          <Logo />
          <span className="text-sm font-mono font-semibold tracking-[0.5px] text-text-bright">
            ARCANUM
          </span>
          <span
            data-testid="shared-deal-link-badge"
            className="text-micro font-mono uppercase tracking-[0.5px] text-accent border border-accent/30 rounded-[2px] bg-accent-dim px-2 py-0.5"
          >
            Shared Deal Link
          </span>
        </div>
        {showHeaderCta && (
          <div className="flex items-center gap-3">
            {/* WS4: invite the visitor into the live chat BEFORE the paywall.
                Dispatches `arcanum:open-narrative-bar`; the NarrativeBar
                listener expands + focuses the composer. Plain button (no
                navigation) — the chat lives on this same page. */}
            <button
              type="button"
              data-testid="header-ask-ai-cta"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('arcanum:open-narrative-bar')
                )
              }
              className="px-3 py-1.5 border border-accent/40 rounded-[2px] bg-accent/10 text-accent text-xs font-mono font-bold tracking-[0.5px] uppercase hover:bg-accent/20 transition-colors"
            >
              Ask the AI about this deal
            </button>
            {/* WS5: existing-account sign-in path, distinct from the prospect
                Unlock CTA. Carries shareId so post-auth linking fires. */}
            <a
              href={authUrlWithShare('/login', shareId)}
              data-testid="header-login-cta"
              onClick={() =>
                posthog.capture('deal_signup_cta_clicked', {
                  source: 'header-login',
                  share_id: shareId,
                })
              }
              className="px-3 py-1.5 border border-panel-border rounded-[2px] text-text-muted text-xs font-mono font-bold tracking-[0.5px] uppercase hover:border-accent/40 hover:text-accent transition-colors no-underline"
            >
              Already have an account? Log in
            </a>
            {/* Prospect Unlock/Sign-up — also carries shareId (WS5/F5) so a
                prospect who signs up here still gets the share linked. */}
            <a
              href={authUrlWithShare('/signup', shareId)}
              data-testid="header-unlock-cta"
              onClick={() =>
                posthog.capture('deal_signup_cta_clicked', {
                  source: 'header',
                  share_id: shareId,
                })
              }
              className="px-3 py-1.5 border border-accent/40 rounded-[2px] bg-accent/10 text-accent text-xs font-mono font-bold tracking-[0.5px] uppercase hover:bg-accent/20 transition-colors no-underline"
            >
              Unlock Full Arcanum Access
            </a>
          </div>
        )}
      </header>
      {tabBar}
      <main className="flex-1 overflow-y-auto flex flex-col">{children}</main>
      {/* Composer sits OUTSIDE `<main>` so its expanded overlay isn't clipped
          by main's overflow (see `composer` prop doc). */}
      {composer}
      <footer
        data-testid="public-chrome-footer"
        className="flex items-center justify-end px-6 py-1.5 border-t border-panel-border bg-bg shrink-0"
      >
        <span className="text-micro font-mono text-text-dim">
          Built on Arcanum
        </span>
      </footer>
    </div>
  );
}
